import cn from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import CustomLink from 'components/CustomLink/CustomLink';
import {
  DeltaGlobalDemoLogo,
  DeltaIndiaDemoLogo,
} from 'components/Header/Demo/DeltaIndiaDemoLogo';
import {
  RenderByExchange,
  RenderForGlobalExchange,
  RenderForIndianExchange,
} from 'components/render/RenderByExchangeType';
import {
  CAREERS_LINK,
  DELTA_EXCHANGE_YOUTUBE_CHANNEL,
  DOCS_URL,
  FIU_ID,
  FIU_LINK,
  IS_DEMO,
  IS_INDIAN_EXCHANGE,
  PROD_URL,
  // LANDING_PAGE_URL,
  SUPPORT_CENTER_LINK_HOME,
} from 'constants/constants';
import routes from 'constants/routes';
import { userSelector } from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';
import {
  DeltaLogoText,
  Instagram,
  TelegramCircleIcon,
  YoutubeCircleIcon,
} from 'styles/assets/icons';
import DeltaIndiaLogo from 'styles/assets/icons/DeltaIndiaLogo';
import LinkedinIcon from 'styles/assets/icons/LinkedinIcon';
import XIcon from 'styles/assets/icons/XIcon';
import DeltaButton from 'UIKit/DeltaButton';

import classes from './appFooter.module.scss';
import FooterWrapper from './appFooter.styles';

// TODO: Refactor bottom list with new parameter hide.
const company = () =>
  [
    {
      text: 'About Us',
      link: `/about-us/`,
      key: 'aboutUs',
    },
    {
      text: 'Affiliate Program',
      link: `/referral-program/`,
      key: 'affiliateProg',
    },
    {
      text: 'Terms of Service',
      link: `/terms-of-use/`,
      key: 'tos',
    },
    {
      text: 'Privacy & Cookies Policy',
      link: `/privacy-policy/`,
      key: 'privacy&CookiesPolicy',
    },
    {
      text: 'Press',
      link: `/press/`,
      key: 'press',
    },
    {
      text: 'Careers',
      link: CAREERS_LINK,
      key: 'careers',
    },
  ].filter(({ key }) => {
    if (key === 'press') {
      return !IS_INDIAN_EXCHANGE;
    }
    if (key === 'careers') {
      return IS_INDIAN_EXCHANGE && !IS_DEMO;
    }
    return true;
  });

const data = [
  {
    text: 'Fees',
    link: IS_DEMO ? `${PROD_URL}/fees` : '/fees/',
    key: 'fees',
    target: '_blank',
  },
  {
    text: 'Contract Specifications',
    link: IS_DEMO ? `${PROD_URL}/contracts` : '/contracts',
    key: 'contractSpecs',
    target: '_blank',
  },
  // {
  //   text: 'Delta Research & Analytics',
  //   link: `/data-analytics`,
  //   key: 'deltaRandA',
  // },
  {
    text: 'Underlying Indices',
    link: IS_DEMO ? `${PROD_URL}/indices` : '/indices/',
    key: 'underlyingIndices',
    target: '_blank',
  },
];

const references = [
  {
    text: 'Support Center',
    link: SUPPORT_CENTER_LINK_HOME,
    key: 'supportCenter',
  },
  {
    text: 'Blog',
    link: IS_DEMO ? `${PROD_URL}/blog` : '/blog/',
    key: 'blog',
  },
  {
    text: 'User Guide',
    link: IS_INDIAN_EXCHANGE
      ? 'https://india.delta.exchange/user-guide'
      : 'https://delta.exchange/user-guide',
    key: 'userGuide',
  },
  {
    text: 'API Documentation',
    link: `${DOCS_URL}/#introduction`,
    key: 'apiDoc',
  },
];

const socialLinksIndian = [
  {
    name: 'x',
    url: 'https://x.com/Delta_Exchange',
    source: <XIcon className="social-link indianSocialIcon" />,
  },
  {
    name: 'instagram',
    url: 'https://www.instagram.com/deltaexchangein',
    source: <Instagram className="social-link indianSocialIcon" />,
  },
  {
    name: 'youtube',
    url: 'https://www.youtube.com/@deltaexchange',
    source: <YoutubeCircleIcon className="social-link indianSocialIcon" />,
  },
  {
    name: 'linkedin',
    url: 'https://www.linkedin.com/company/delta-exchange/',
    source: <LinkedinIcon className="social-link indianSocialIcon" />,
  },
];

const socialLinksGlobal = [
  {
    name: 'x',
    url: 'https://x.com/Delta_Exchange',
    source: <XIcon className={cn('social-link', classes.globalIcon, classes.xIcon)} />,
  },
  {
    name: 'instagram',
    url: 'https://instagram.com/deltaexchangeofficial',
    source: <Instagram className={cn('social-link', classes.globalIcon)} />,
  },
  {
    name: 'telegram',
    url: 'https://t.me/deltaexchat',
    source: <TelegramCircleIcon className={cn('social-link', classes.globalIcon)} />,
  },
  {
    name: 'youtube',
    url: DELTA_EXCHANGE_YOUTUBE_CHANNEL,
    source: <YoutubeCircleIcon className={cn('social-link', classes.globalIcon)} />,
  },
];

const AppFooter = () => {
  const user = useAppSelector(userSelector);
  const isUserAvailable = user.id;

  const { t } = useTranslation(['headerFooter']);

  const getLocalizedText = (type, localeKey) => {
    const str = `headerFooter:footer.${type}.${localeKey}`;
    return t(str);
  };

  return (
    (<FooterWrapper>
      <div className="delta-contact">
        <div className="header-wrapper col-lg-24">
          <RenderByExchange
            global={IS_DEMO ? <DeltaGlobalDemoLogo /> : <DeltaLogoText />}
            indian={
              IS_DEMO ? (
                <DeltaIndiaDemoLogo />
              ) : (
                <DeltaIndiaLogo className="headerLogoIndianExchange" />
              )
            }
          />
        </div>
        <p className="delta-text">
          <RenderByExchange
            global={`${t('headerFooter:footer.description')}`}
            indian={
              <Trans
                i18nKey="headerFooter:footer.fiuText"
                values={{
                  fiuId: FIU_ID,
                }}
              >
                <DeltaButton
                  variant="link-t1"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="fiu-link"
                  href={FIU_LINK}
                  className={classes.fiuLink}
                >
                  FIU
                </DeltaButton>
              </Trans>
            }
          />
        </p>
        <RenderForIndianExchange>
          <p className={classes.address}>
            {t('headerFooter:footer.company.indiaCopyRight')}
          </p>
        </RenderForIndianExchange>
        <RenderForGlobalExchange>
          <div className="social-icons-and-copyright-text">
            <div className="delta-social-links">
              <ul>
                {socialLinksGlobal.map(item => (
                  <li key={item.name} data-palette="AppFooter">
                    <CustomLink
                      target="blank"
                      rel="noopener noreferrer"
                      href={item.url}
                      data-testid={item.name}
                    >
                      {item.source}
                    </CustomLink>
                  </li>
                ))}
              </ul>
            </div>

            <span className={classes.copyrightText}>
              &copy; {new Date().getFullYear()} {t('headerFooter:footer.copyRight')}{' '}
            </span>
          </div>
        </RenderForGlobalExchange>
      </div>
      <div className="delta-info">
        <div className="company delta-info-item">
          <h4>{getLocalizedText('company', 'title')}</h4>
          <ul>
            {company(isUserAvailable).map(item => (
              <li key={item.key} data-palette="AppFooter">
                {' '}
                <CustomLink target="blank" href={item.link} data-testid={item.key}>
                  {getLocalizedText('company', item.key)}
                </CustomLink>
              </li>
            ))}
          </ul>
        </div>
        <div className="data delta-info-item">
          <h4>{getLocalizedText('data', 'title')}</h4>
          <ul>
            {data
              .filter(({ key }) => !(IS_INDIAN_EXCHANGE && ['deltaRandA'].includes(key)))
              .map(item => (
                <li key={item.key} data-palette="AppFooter">
                  {' '}
                  <CustomLink target="blank" href={item.link} data-testid={item.key}>
                    {getLocalizedText('data', item.key)}
                  </CustomLink>
                </li>
              ))}
            <li>
              <NavLink to={routes.expired_futures_and_options} target="_blank">
                {getLocalizedText('data', 'settlementPrices')}
              </NavLink>
            </li>
          </ul>
        </div>

        <div className="references delta-info-item">
          <h4>{getLocalizedText('references', 'title')}</h4>
          <ul>
            {references.map(item => (
              <li key={item.key} data-palette="AppFooter">
                {' '}
                <CustomLink target="blank" href={item.link} data-testid={item.key}>
                  {getLocalizedText('references', item.key)}
                </CustomLink>
              </li>
            ))}
          </ul>
        </div>
        <RenderForIndianExchange>
          <div className="social-links-container delta-info-item">
            <h4>Social Links</h4>
            <ul>
              {socialLinksIndian.map(item => (
                <li key={item.name} data-palette="AppFooter">
                  <CustomLink
                    target="blank"
                    rel="noopener noreferrer"
                    href={item.url}
                    data-testid={item.name}
                    className="social-info"
                  >
                    <span>{item.source}</span>
                    <p>{item.name}</p>
                  </CustomLink>
                </li>
              ))}
            </ul>
          </div>
        </RenderForIndianExchange>
      </div>
      <div className="social-icons-and-copyright-text-mobile-tablet">
        <div className="delta-social-links">
          <ul>
            {socialLinksGlobal.map(item => (
              <li key={item.name} data-palette="AppFooter">
                <CustomLink
                  target="blank"
                  rel="noopener noreferrer"
                  href={item.url}
                  data-testid={item.name}
                >
                  <img src={item.source} alt={item.name} width="50px" height="50px" />
                </CustomLink>
              </li>
            ))}
          </ul>
        </div>
        <span className={classes.copyrightText}>
          &copy; {new Date().getFullYear()} {t('headerFooter:footer.copyRight')}{' '}
        </span>
      </div>
    </FooterWrapper>)
  );
};

export default AppFooter;
